import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useActionDispatch, useDocumentCategoryOptions } from '../../../hooks/utils'
import { DocumentCategoryKeys } from '../../../model/DocumentCategory'
import { SelectOption } from '../../../model/SelectOption'
import { CheckboxState, DatePicker, IconButton, RoundedButton, SelectInput } from '../../shared'
import { MailClassificationProps } from './MailClassification.model'
import { MailClassificationAttachmentItem } from './MailClassificationAttachmentItem'
import { PatientDropDownItemSelector } from '../../patient'
import { MailPatientInfo } from './MailPatientInfo'
import { MailPatientDetection } from './MailPatientDetection'
import { MailClassificationAttachment, MailPatient } from '../../../model/Mail'
import { CreatePatientThroughMailModal } from '../../insi'
import { MailClassificationModal } from './MailClassificationModal'
import {
  mapMailAttachmentsToSelectedAttachments,
  mapMailAttachmentToSelectedAttachment,
} from './MailClassification.utilities'
import { useClassifyAttachments } from '../../../hooks/queries/mail'
import { isDefined } from '../../../misc/functions.utilities'
import { useCreatePatientFromMail } from '../../../hooks/queries/mail/useCreatePatientFromMail.query'
import { FeaturesType } from '../../../model/Preference'
import { useUserEnabledFeature } from '../../../hooks/utils/user'
import { isINSIValidated } from '../../../misc/patient.utilities'
import type { InsiPatient } from '../../../model/Insi'
import { PatientFormFields } from '../../patient/PatientForm/PatientForm.model'
import type { ComparisonResultItem } from '../../../model/ComparisonResult.type'
import { InsiReviewAndValidateModal } from '../../patient/PatientForm/IdentityStatusBlock/InsiReviewAndValidateModal'
import { useUpdatePatient } from '../../../hooks/queries/patients'
import { mailKeys } from '../../../hooks/queries/mail/mail.keys'
import { queryClient } from '../../../queryClient'
import { addValid } from '../../../store/message'
import { inputInseeNumberFormatter } from '@follow/cdk'
import { partialMailPatientModalAtom } from '../../../state/mail'
import { useAtomValue } from 'jotai'
import { DATE_FORMAT_FRONT_SHORT, formatFr, parseApiFormat } from '../../../misc/date.utilities'
import {
  compareOIDValues,
  deserializeInsiPatient,
  deserializeInsiPayload,
  formatNamesList,
} from '../../patient/PatientForm/IdentityStatusBlock/IdentityStatusBlock.utils'
import { compareObjects } from '../../../misc/objects.utilities'
import { patientInsiToPatientMapper } from '../../patient/PatientForm/PatientForm.mapper'
import { CustomFile } from '../../../model/File'
import { LightPatient } from '../../../model/Patient'
import { useIdentityCheckWithTaskCreation } from '../../../hooks/queries/insi/useIdentityCheckWithTaskCreation'

export const MailClassification: FC<MailClassificationProps> = ({ inUseMail, isFetching }) => {
  const { attachments, patient: mailPatient, id: mailId, date: mailDate, isTrusted } = inUseMail
  const { identityStatus } = mailPatient || {}

  const categoriesOptions = useDocumentCategoryOptions()
  const defaultCategory = useMemo(
    () =>
      categoriesOptions.find(
        (option: SelectOption<DocumentCategoryKeys>) =>
          option.value === DocumentCategoryKeys.ReceivedDocument,
      ),
    [categoriesOptions],
  )

  const [documentCategory, setDocumentCategory] = useState<
    SelectOption<DocumentCategoryKeys> | undefined
  >(defaultCategory)
  const [date, setDate] = useState(new Date())
  const [selectedPatient, setSelectedPatient] = useState<LightPatient | MailPatient | undefined>()
  const [selectedAttachments, setSelectedAttachments] = useState<MailClassificationAttachment[]>([])
  const [showClassificationModal, setShowClassificationModal] = useState<boolean>(false)
  const [patientHasBeenCreated, setPatientHasBeenCreated] = useState(false)
  const [showCompareINSIModal, setShowCompareINSI] = useState(false)
  const [compareINSIResult, setCompareINSIResult] =
    useState<ComparisonResultItem<InsiPatient & PatientFormFields, string>[]>()
  const [pendingPatientUpdate, setPendingPatientUpdate] = useState<PatientFormFields>()

  // --- Hooks and Queries ---
  const dispatchValid = useActionDispatch(addValid)
  const { mutateAsync: updatePatient } = useUpdatePatient()
  const { mutateAsync: asyncIdentityCheckWithTaskCreation } = useIdentityCheckWithTaskCreation()
  const { mutate: classifyDocuments, isPending: isClassifying } = useClassifyAttachments()
  const { mutate: createPatientFromMail, status: createPatientFromMailStatus } =
    useCreatePatientFromMail()
  const partialPatientModalState = useAtomValue(partialMailPatientModalAtom)

  // --- Feature Flags ---
  const autoInsi = useUserEnabledFeature(FeaturesType.mssAutoInsiFetch)
  const insiService = useUserEnabledFeature(FeaturesType.insiValidation)
  const mssExtractPatient = useUserEnabledFeature(FeaturesType.mssExtractPatient)
  const mssSmtp = useUserEnabledFeature(FeaturesType.mssSmtp)

  // --- Derived State ---
  const isPatientStatusValidated = identityStatus ? isINSIValidated(identityStatus) : false
  const isClassifyButtonDisabled =
    isClassifying ||
    !selectedPatient ||
    !documentCategory ||
    !date ||
    selectedAttachments.length === 0

  /**
   * Resets the form to its default values based on the current mail.
   */
  const setDefaultFormValues = useCallback(() => {
    if (mailDate?.date === null) return
    setDate(new Date(mailDate?.date))
    setSelectedAttachments(mapMailAttachmentsToSelectedAttachments(attachments))
    setDocumentCategory(defaultCategory)
    setSelectedPatient(mailPatient?.id ? mailPatient : undefined)
  }, [mailDate?.date, attachments, defaultCategory, mailPatient])

  /**
   * Creates a new patient from the mail's detected patient information.
   */
  const handlePatientCreation = useCallback(() => {
    if (!mailPatient || !mssExtractPatient) return

    createPatientFromMail(
      {
        mailId,
        patient: mailPatient,
        bypassCheck: false,
        insiInError: false,
        recipient: inUseMail.emailFrom,
        sender: inUseMail.emailTo,
        fromTrustedSource: isTrusted,
      },
      {
        onSuccess(data) {
          if (data) {
            setSelectedPatient(data)
            partialPatientModalState?.onSubmit(false)
            queryClient.refetchQueries({ queryKey: mailKeys.detail(mailId) })
            setPatientHasBeenCreated(true)
          }
        },
      },
    )
  }, [
    mailPatient,
    isTrusted,
    mssExtractPatient,
    createPatientFromMail,
    mailId,
    inUseMail.emailFrom,
    inUseMail.emailTo,
    partialPatientModalState,
  ])

  /**
   * Fetches the patient's identity from the INSI service and compares the data.
   * If there are differences, it shows a modal for review and validation.
   */
  const fetchPatientIdentity = useCallback(async () => {
    if (!mailPatient) return

    try {
      const insiData = await asyncIdentityCheckWithTaskCreation({ patient: mailPatient })
      if (!insiData?.patient) return

      const insiPatient = insiData?.patient
      const birthDayPatient = mailPatient.birthDate
        ? formatFr(parseApiFormat(mailPatient.birthDate), DATE_FORMAT_FRONT_SHORT)
        : null
      const insiNumberPatient = mailPatient.inseeNumber
        ? inputInseeNumberFormatter(mailPatient.inseeNumber)
        : mailPatient.inseeNumber

      const dataPatient = {
        ...mailPatient,
        birthDate: birthDayPatient,
        inseeNumber: insiNumberPatient,
      } as PatientFormFields

      const deserializedPatient = deserializeInsiPatient(insiPatient, dataPatient)
      const deserializedInsiPayload = deserializeInsiPayload(insiPatient)

      let insiCompareResult = compareObjects(
        deserializedInsiPayload as InsiPatient & { otherFirstNames?: string },
        dataPatient,
      )

      if (insiCompareResult) {
        insiCompareResult = compareOIDValues(insiCompareResult)
        setCompareINSIResult(insiCompareResult)
        setPendingPatientUpdate(deserializedPatient)
        setShowCompareINSI(true)
      }
    } catch (err) {
      console.error(err)
    }
  }, [mailPatient, asyncIdentityCheckWithTaskCreation])
  /**
   * Classifies the selected attachments with the chosen category, date, and patient.
   * @param {MailClassificationAttachment[]} attachmentsToClassify The attachments to classify.
   */
  const handleClassify = useCallback(
    (attachmentsToClassify: MailClassificationAttachment[]) => {
      if (documentCategory && selectedPatient?.id && date && attachmentsToClassify.length > 0) {
        classifyDocuments({
          messageId: mailId,
          selectedAttachments: attachmentsToClassify,
          patientId: selectedPatient.id,
          documentCategoryValue: documentCategory.value,
          date,
        })
        setShowClassificationModal(false)
        setDefaultFormValues()
      }
    },
    [classifyDocuments, date, documentCategory, mailId, selectedPatient, setDefaultFormValues],
  )

  const handleSelectFile = (selectedItem: CustomFile) => {
    const selection: MailClassificationAttachment[] = selectedAttachments.some(
      (item: MailClassificationAttachment) => item.id === selectedItem.id,
    )
      ? [
          ...selectedAttachments.filter(
            (item: MailClassificationAttachment) => item.id !== selectedItem.id,
          ),
        ]
      : [...selectedAttachments, mapMailAttachmentToSelectedAttachment(selectedItem)]

    setSelectedAttachments(selection)
  }

  /**
   * Updates the patient's information from the INSI validation modal.
   * @param {PatientFormFields} patientData The updated patient data.
   */
  const handlePatientUpdateFromConfirmationModal = useCallback(
    async (patientData: PatientFormFields, firstName?: string) => {
      if (!mailPatient?.id || !mssExtractPatient) return
      const updated = patientInsiToPatientMapper(patientData)
      try {
        const updatedPatient = {
          ...updated,
          birthFirstName: firstName ? firstName : updated.birthFirstName,
          otherFirstNames: formatNamesList(updated.otherFirstNames?.split(' ')).join(' '),
        }
        await updatePatient({
          id: mailPatient.id,
          payload: updatedPatient,
          params: { external: true, forceInsiCheck: false },
        })

        queryClient.refetchQueries({ queryKey: mailKeys.detail(mailId) })
        setPendingPatientUpdate(undefined)
        setCompareINSIResult(undefined)
        setShowCompareINSI(false)
        dispatchValid(
          'Succès',
          'Les informations d’identité du patient ont été mises à jour avec succès',
        )
      } catch (err) {
        const { message } = err as Error
        console.error(message)
      }
    },
    [dispatchValid, mailPatient, mailId, mssExtractPatient, updatePatient],
  )

  // --- Effects ---
  // Set default form values on initial render and when mail changes.
  useEffect(() => {
    setDefaultFormValues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set default category if it's undefined.
  useEffect(() => {
    if (!isDefined(documentCategory) && isDefined(defaultCategory)) {
      setDocumentCategory(defaultCategory)
    }
  }, [defaultCategory, documentCategory])

  // Automatically create a patient if conditions are met.
  useEffect(() => {
    if (
      autoInsi &&
      !isFetching &&
      insiService === 'insiValidationInApp' &&
      mssExtractPatient &&
      isDefined(mailPatient) &&
      !isDefined(mailPatient.id) &&
      createPatientFromMailStatus === 'idle'
    ) {
      handlePatientCreation()
    }
  }, [
    autoInsi,
    handlePatientCreation,
    insiService,
    isFetching,
    mailPatient,
    mssExtractPatient,
    createPatientFromMailStatus,
  ])

  // Automatically fetch patient identity for INSI validation if conditions are met.
  useEffect(() => {
    if (
      autoInsi &&
      !isFetching &&
      insiService === 'insiValidationInApp' &&
      mssExtractPatient &&
      isDefined(mailPatient) &&
      mailPatient.id &&
      !isPatientStatusValidated &&
      !patientHasBeenCreated &&
      !mailPatient?.identityStatus?.fictitious &&
      !mailPatient?.identityStatus?.suspicious
    ) {
      fetchPatientIdentity()
    }
  }, [
    mssExtractPatient,
    isFetching,
    insiService,
    autoInsi,
    mailPatient,
    patientHasBeenCreated,
    isPatientStatusValidated,
    fetchPatientIdentity,
  ])

  return (
    <div className="bg-white w-full h-full space-y-4">
      {inUseMail.patient && mssExtractPatient && (
        <div className="mb-5">
          <div className="mb-5">
            <MailPatientDetection
              isFetching={isFetching}
              patient={inUseMail.patient}
              onUpdate={fetchPatientIdentity}
              hasPatientBeenCreated={patientHasBeenCreated}
              onCreate={handlePatientCreation}
            />
          </div>
          <MailPatientInfo patient={inUseMail.patient} />
        </div>
      )}
      <span className="text-shades-3 text-base font-medium">CLASSER LE(S) DOCUMENT(S)</span>
      <div className="mt-2">
        {inUseMail?.attachments.map((item) => (
          <div className="mt-2" key={item.id}>
            <MailClassificationAttachmentItem
              item={item}
              checked={
                selectedAttachments?.some(({ id }) => item.id === id)
                  ? CheckboxState.CHECKED
                  : CheckboxState.UNCHECKED
              }
              selectItem={handleSelectFile}
            />
          </div>
        ))}
      </div>
      <PatientDropDownItemSelector
        selectedPatient={selectedPatient}
        setSelectedPatient={setSelectedPatient}
      />
      <SelectInput
        title="Catégorie du document"
        value={documentCategory}
        icon={documentCategory?.icon ?? 'documentText'}
        options={categoriesOptions}
        onSelect={setDocumentCategory}
        testId="mail-attachment-document_category"
      />
      <DatePicker
        value={date}
        colorPreset="light"
        icon="calendar"
        showCalendarButton={false}
        label="Date"
        onChange={(value) => {
          if (value) {
            setDate(value)
          }
        }}
        inputType="input"
        testId="date_picker-mss-mail_classification-date"
      />
      <div className="py-4">
        <div className="flex">
          <RoundedButton
            disabled={isClassifyButtonDisabled}
            label={isClassifying ? 'Classification en cours... ' : 'Classer ce(s) document(s)'}
            onClick={() => handleClassify(selectedAttachments)}
            appearance={mssSmtp ? 'left' : 'standalone'}
            testId="button-mss-documents-classify"
          />
          {mssSmtp && (
            <IconButton
              disabled={isClassifyButtonDisabled}
              icon="caret"
              rotate={180}
              appearance="right"
              theme="primary"
              showOptionsCaret={false}
              options={[
                {
                  label: 'Renommer et classer',
                  onClick: () => setShowClassificationModal(true),
                },
              ]}
              testId="button-mss_smtp-attachments-classify"
            />
          )}
        </div>
      </div>
      <MailClassificationModal
        display={showClassificationModal}
        attachments={selectedAttachments}
        onClose={() => setShowClassificationModal(false)}
        onSubmit={(values: MailClassificationAttachment[]) => {
          setSelectedAttachments([...values])
          handleClassify(values)
        }}
        testId={`mail-classification-modal`}
      />
      <CreatePatientThroughMailModal
        display={isDefined(partialPatientModalState)}
        onSubmit={() => partialPatientModalState?.onSubmit(true)}
        onClose={() => partialPatientModalState?.onSubmit(false)}
      />
      {compareINSIResult && (
        <InsiReviewAndValidateModal
          display={showCompareINSIModal}
          comparisonResults={compareINSIResult}
          onSubmit={(firstName) => {
            pendingPatientUpdate &&
              handlePatientUpdateFromConfirmationModal(pendingPatientUpdate, firstName)
            setCompareINSIResult(undefined)
            setShowCompareINSI(false)
            return
          }}
          onClose={() => {
            setPendingPatientUpdate(undefined)
            setCompareINSIResult(undefined)
            setShowCompareINSI(false)
            return
          }}
        />
      )}
    </div>
  )
}
